import { ReactElement, ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Paragraph, Skeleton } from '@digdir/designsystemet-react';
import { useDocumentationQuery } from '../../hooks/queries/useArchiveQuery';
import { useHmsReportPdfMutation } from '../../hooks/mutations/useHmsReportPdfMutation';
import { useHmsReportPdf } from '../../hooks/useHmsReportPdf';
import { questionLanguageFor } from '../FormSubmission/workEnvironmentFormView';
import { plansCanChange } from '../WorkEnvironmentSurveyDetail/areasLeftView';
import { ControlPart, ParticipationPart, SensitivePart, SignaturePart } from './components/ClosingParts';
import { ReportHeader, ReportSummary } from './components/ReportIntro';
import { RequirementsPart } from './components/RequirementsPart';
import { PlansPart, RiskPart, StepsPart } from './components/WorkParts';
import classes from './InspectionReport.module.css';

/** The page of its own is the main content. Inside the evaluation it is part of it. */
const Content = ({ embedded, children }: { embedded: boolean; children: ReactNode }): ReactElement =>
  embedded ? (
    <div className={classes.embedded}>{children}</div>
  ) : (
    <main id="main" className={classes.main}>
      {children}
    </main>
  );

type Props = {
  surveyId: number;
  /** The way back, when the report has a page of its own */
  back?: ReactNode;
  embedded?: boolean;
  /** Where a plan for an area is made, for those who make plans */
  planLink?: (areaKey: string) => string;
};

/**
 * The inspection report for one team: the requirements and how the team met them, and the
 * work behind them. «Vis bare mangler» keeps what remains, for the one who follows up.
 */
export const TeamInspectionReport = ({ surveyId, back, embedded = false, planLink }: Props): ReactElement => {
  const { t, i18n } = useTranslation();
  const [onlyGaps, setShowOnlyGaps] = useState(false);
  const { data: report, isLoading, isError } = useDocumentationQuery(surveyId, questionLanguageFor(i18n.language));
  const pdf = useHmsReportPdfMutation('work-environment-inspection-report', surveyId);
  const forPdf = useHmsReportPdf(Boolean(report));
  // The PDF has the whole report, whatever the screen shows
  const showOnlyGaps = onlyGaps && !forPdf;
  // The PDF and a locked report are documentation, so they link nowhere. Plans can only be made in step 7 and 8.
  const linksToPlans =
    !forPdf && report && !report.locked && plansCanChange(report.survey.status) ? planLink : undefined;

  return (
    <div className={embedded ? undefined : classes.page} data-size="md" data-hms-report>
      <div className={classes.toolbar} data-print="false">
        <div className={classes.toolbarInner}>
          {back}
          {report && (
            <div className={classes.actions}>
              <Button
                variant={showOnlyGaps ? 'primary' : 'secondary'}
                data-size="sm"
                aria-pressed={showOnlyGaps}
                onClick={() => setShowOnlyGaps(!showOnlyGaps)}
              >
                {t('workEnvironmentInspection.toolbar.onlyGaps')}
              </Button>
              <Button variant="secondary" data-size="sm" loading={pdf.isPending} onClick={() => pdf.mutate()}>
                {t('workEnvironmentInspection.toolbar.print')}
              </Button>
            </div>
          )}
          {pdf.isError && (
            <Alert data-color="danger" data-size="sm">
              <Paragraph data-size="sm">{t('workEnvironmentInspection.toolbar.pdfFailed')}</Paragraph>
            </Alert>
          )}
        </div>
      </div>
      <Content embedded={embedded}>
        {isLoading && (
          <div aria-busy="true" className={classes.article}>
            <span className="ds-sr-only">{t('workEnvironmentInspection.loading')}</span>
            <Skeleton variant="rectangle" width="100%" height={480} />
          </div>
        )}
        {isError && (
          <Alert data-color="danger" className={classes.article}>
            <Paragraph>{t('workEnvironmentInspection.error')}</Paragraph>
          </Alert>
        )}
        {report && (
          <article className={classes.article} aria-labelledby="report-title">
            <ReportHeader report={report} titleLevel={embedded ? 2 : 1} />
            <ReportSummary report={report} />
            <RequirementsPart report={report} showOnlyGaps={showOnlyGaps} planLink={linksToPlans} />
            {!showOnlyGaps && (
              <>
                <StepsPart report={report} />
                <RiskPart report={report} />
                <PlansPart report={report} />
                <ParticipationPart report={report} />
                <SensitivePart report={report} />
                <ControlPart report={report} />
              </>
            )}
            <SignaturePart report={report} />
          </article>
        )}
      </Content>
    </div>
  );
};
